import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * Live transport-quote hook.
 *
 * Submits the delivery + items to /api/transport/quote → async job.
 * Polls /api/transport/quote/{job_id} every 2s until done|error.
 * Re-submits automatically when delivery/cart hash changes (debounced 800ms).
 *
 * Returns { loading, options, error, refresh, selected, setSelected }.
 * Caller is responsible for displaying options + calling setSelected so the
 * checkout form knows the chosen tier + price.
 */
export function useTransportQuote(delivery, items) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [meta, setMeta] = useState(null);
  const pollRef = useRef(null);
  const debounceRef = useRef(null);
  const lastKeyRef = useRef("");

  const fetchQuote = async (force = false) => {
    // Drop the previous poll if any
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!delivery?.postal || !delivery?.country || !(items && items.length)) {
      setOptions([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.post("/transport/quote", { delivery, items, force });
      const jobId = r.data?.job_id;
      if (!jobId) {
        // Cached → result is immediately available
        if (r.data?.status === "done") {
          applyResult(r.data.result);
        } else {
          setOptions([]);
          setError("Geen vervoersaanbieder beschikbaar");
        }
        setLoading(false);
        return;
      }
      // Poll
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        if (attempts > 60) {   // 120s timeout (cold-start Playwright ~30-40s + scrape 30-50s)
          clearInterval(pollRef.current);
          pollRef.current = null;
          setError("Timeout — carrier reageert niet binnen 120 seconden");
          setLoading(false);
          return;
        }
        try {
          const s = await api.get(`/transport/quote/${jobId}`);
          if (s.data.status === "done") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            applyResult(s.data.result);
            setLoading(false);
          } else if (s.data.status === "error") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setError(s.data.error || "Onbekende fout");
            setOptions([]);
            setLoading(false);
          }
        } catch (e) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setError(e?.response?.data?.detail || "Verbinding mislukt");
          setLoading(false);
        }
      }, 2000);
    } catch (e) {
      setLoading(false);
      setError(e?.response?.data?.detail || "Quote aanvragen mislukt");
    }
  };

  const applyResult = (result) => {
    const opts = result?.options || [];
    setOptions(opts);
    setMeta({
      fromCache: result?.from_cache,
      source: result?.source,
      asOf: result?.as_of,
      distanceKm: result?.distance_km,
    });
    // Auto-select cheapest if nothing selected yet (or if previous selection no longer exists)
    if (opts.length > 0) {
      setSelected((prev) => {
        const stillThere = prev && opts.find((o) => o.carrier === prev.carrier && o.label === prev.label);
        if (stillThere) return prev;
        return opts[0];
      });
    } else {
      setSelected(null);
    }
  };

  // Debounce re-fetch when inputs change
  useEffect(() => {
    const key = JSON.stringify({
      country: delivery?.country, postal: delivery?.postal, city: delivery?.city,
      street: delivery?.street, number: delivery?.number,
      items: (items || []).map((i) => `${i.sku}x${i.quantity}@${i.meta?.weight_g || 0}`),
    });
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchQuote(false), 800);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    delivery?.country, delivery?.postal, delivery?.city, delivery?.street, delivery?.number,
    items,
  ]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return { loading, options, error, selected, setSelected, meta, refresh: () => fetchQuote(true) };
}
